import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { projectsApi, salesApi, unwrap, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useDayLock } from "../hooks/useDayLock";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import {
  Button,
  Empty,
  Input,
  Loader,
  Modal,
  Pagination,
  Progress,
  Select,
  StatusPill,
} from "../components/ui";
import { ConfirmDialog, LockedNote, RecordActions } from "../components/actions";
import { CollectInstallmentModal } from "../components/CollectInstallment";
import { SaleForm, emptySaleForm, saleMoneyChanged, salePayload, saleToForm } from "../components/forms";
import { COPY, hasInstallments } from "../utils/copy";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { calcSalePreview, formatMoney, paidPercent } from "../utils/format";

export default function Sales() {
  const { isBuilder, query, ready } = useBuilderQuery();
  const toast = useToast();
  const { isClosed, reload: reloadDays } = useDayLock();
  const { busy, run } = useAsyncAction();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [page, setPage] = useState(1);
  const liveSearch = useDebouncedValue(search);
  const [form, setForm] = useState(emptySaleForm);
  const [editing, setEditing] = useState(null);
  const [collecting, setCollecting] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const openNew = params.get("new") === "1";
  const preview = useMemo(() => calcSalePreview(form), [form]);
  const moneyLocked = Boolean(editing && isClosed(editing.date));

  const load = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [salesRes, projectRes] = await Promise.all([
        salesApi.list({ ...query, search: liveSearch, status, page, limit: 12 }),
        isBuilder ? projectsApi.list({ limit: 100 }) : Promise.resolve({ data: { items: [] } }),
      ]);
      setItems(unwrapList(salesRes).items);
      setPagination(unwrapList(salesRes).pagination);
      setProjects(unwrapList(projectRes).items);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, liveSearch, query.builderId]);

  useEffect(() => {
    setPage(1);
  }, [liveSearch]);

  useEffect(() => {
    const collectId = params.get("collect");
    if (!collectId) {
      setCollecting(null);
      return undefined;
    }
    const found = items.find((item) => item.id === collectId);
    if (found) {
      setCollecting(found);
      return undefined;
    }
    let cancelled = false;
    salesApi
      .get(collectId)
      .then((res) => {
        if (!cancelled) setCollecting(unwrap(res));
      })
      .catch(() => {
        if (!cancelled) setCollecting(null);
      });
    return () => {
      cancelled = true;
    };
  }, [params, items]);

  const closeModal = () => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("new");
      return next;
    });
    setEditing(null);
    setForm(emptySaleForm);
  };

  const save = async (event) => {
    event.preventDefault();
    if (preview.invalid) {
      toast.error("Down payment cannot be more than the total price");
      return;
    }

    const result = await run(async () => {
      if (editing) {
        const moneyChanged = !moneyLocked && saleMoneyChanged(form, editing);
        return salesApi.update(
          editing.id,
          salePayload(form, {
            includeProject: false,
            includeMoney: moneyChanged,
            includeDownPayment: moneyChanged && !hasInstallments(editing),
            includePlan: false,
          })
        );
      }
      return salesApi.create(salePayload(form));
    }, editing ? "Sale updated." : "Sale saved. Down payment went into today's book.");

    if (!result) return;
    closeModal();
    reloadDays();
    load();
  };

  const closeCollect = () => {
    setCollecting(null);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("collect");
      return next;
    });
  };

  const remove = async () => {
    if (!pendingDelete) return;
    const result = await run(() => salesApi.remove(pendingDelete.id), "Sale deleted. Daily book updated.");
    if (result) {
      setPendingDelete(null);
      reloadDays();
      load();
    }
  };

  const modalOpen = openNew || Boolean(editing);

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Customers and installments</p>
          <h1>Sales</h1>
        </div>
        {isBuilder ? (
          <Button icon="plus" onClick={() => setParams({ new: "1" })}>
            New sale
          </Button>
        ) : null}
      </header>

      <form className="filters" onSubmit={(event) => event.preventDefault()}>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search customer, plot or phone"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          <option value="active">Still paying</option>
          <option value="completed">Paid off</option>
        </Select>
      </form>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty title="No sales yet" text="Add a customer, plot and down payment to start." />
      ) : (
        <div className="sale-grid">
          {items.map((sale) => {
            const locked = isClosed(sale.date);
            return (
              <article key={sale.id} className="sale-card">
                <div className="sale-card-main">
                  <div className="sale-card-top">
                    <h3>{sale.buyerName}</h3>
                    <StatusPill status={sale.status} />
                  </div>
                  <p>
                    {sale.propertyName} · {sale.project?.name || "Project"}
                    {sale.buyerPhone ? ` · ${sale.buyerPhone}` : ""}
                    {locked ? " · Closed day" : ""}
                  </p>
                  <Progress value={paidPercent(sale.receivedAmount, sale.totalAmount)} />
                  <div className="sale-meta">
                    <span>
                      Total
                      <b>{formatMoney(sale.totalAmount)}</b>
                    </span>
                    <span>
                      Remaining
                      <b>{formatMoney(sale.remainingAmount)}</b>
                    </span>
                    <span>
                      Monthly
                      <b>{formatMoney(sale.monthlyInstallment)}</b>
                    </span>
                  </div>
                </div>
                <RecordActions
                  disableDelete={locked}
                  onEdit={() => {
                    setEditing(sale);
                    setForm(saleToForm(sale));
                  }}
                  onDelete={() => setPendingDelete(sale)}
                >
                  {sale.status === "active" && sale.remainingAmount > 0 ? (
                    <Button
                      className="btn-sm"
                      onClick={() => {
                        setCollecting(sale);
                        setParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.set("collect", sale.id);
                          return next;
                        });
                      }}
                    >
                      Collect
                    </Button>
                  ) : null}
                  <Link className="btn btn-ghost btn-sm" to={`/sales/${sale.id}/statement`}>
                    Statement
                  </Link>
                  <Link className="btn btn-ghost btn-sm" to={`/sales/${sale.id}`}>
                    Open
                  </Link>
                </RecordActions>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />

      <Modal
        open={modalOpen}
        title={editing ? "Edit sale" : "New sale"}
        onClose={closeModal}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit" form="sale-form" disabled={busy || preview.invalid}>
              Save sale
            </Button>
          </>
        }
      >
        <LockedNote show={moneyLocked} />
        <SaleForm
          form={form}
          onChange={setForm}
          onSubmit={save}
          projects={projects}
          showProject={!editing}
          showPlan={!editing}
          moneyLocked={moneyLocked}
          downPaymentLocked={Boolean(editing && hasInstallments(editing))}
        />
      </Modal>

      <CollectInstallmentModal
        sale={collecting}
        open={Boolean(collecting)}
        onClose={closeCollect}
        onSaved={load}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete sale?"
        text={COPY.deleteSale}
        busy={busy}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
